import { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Laboratory } from '@/types/product';
import { dataService } from '@/lib/data/DataService';
import { Globe } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Laboratories = () => {
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLaboratories();
  }, []);

  const fetchLaboratories = async () => {
    try {
      const labsData = await dataService.getLaboratories();
      setLaboratories(labsData);
    } catch (error) {
      console.error('Error fetching laboratories:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Laboratoires partenaires | LeaderProMed Mauritanie</title>
        <meta name="description" content="Découvrez nos laboratoires partenaires en Mauritanie et Afrique du Nord, leaders dans la recherche et la distribution médicale." />
        <meta property="og:title" content="Laboratoires partenaires | LeaderProMed Mauritanie" />
        <meta property="og:description" content="Découvrez nos laboratoires partenaires en Mauritanie et Afrique du Nord, leaders dans la recherche et la distribution médicale." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />

        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-2">Nos Laboratoires Partenaires</h1>
            <p className="text-gray-600 text-center mb-12">
              Découvrez nos partenaires de confiance dans le domaine pharmaceutique
            </p>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {laboratories.map((lab) => (
                  <Card key={lab.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-48 relative bg-gray-100">
                      <img
                        src={lab.logo}
                        alt={lab.name}
                        className="w-full h-full object-contain p-4"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl">{lab.name}</CardTitle>
                      <p className="text-sm text-gray-500">{lab.country}</p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        {lab.description}
                      </p>
                      {lab.website && (
                        <a
                          href={lab.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-700"
                        >
                          <Globe className="w-4 h-4 mr-2" />
                          Visiter le site web
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loading && laboratories.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun laboratoire n'est disponible pour le moment.</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Laboratories; 